/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { When } from "react-if";
import { Box, theme, Text, Select, Spacer, Sidebar } from "@nordcloud/gnui";
import { KpiTimePeriod, KpiTargetSelection } from "~/generated/graphql";
import {
  BreadcrumbsBox,
  BrickLoader,
  CurrentTarget,
  DataDisplayWrapper,
  NoData,
  NoKPI,
  UniversalWrap,
  WorstOrgUnits,
  KpiDashboardChart,
  KpiTarget,
  getKpiFetchConfig,
} from "~/components";
import { dateFormat, getKpiType, KpiType } from "~/constants";
import { useQueryState } from "~/hooks";
import { isNotEmpty, isNotNil } from "~/tools";
import {
  useBusinessContexts,
  useKpiData,
  useAddKpiTarget,
  KpiTargets,
  KpiWidget,
  useArchiveTarget,
  useUpdateKpiTarget,
  KPITargetInput,
  KpiMaturityLevel,
  KpiQueryState,
  TargetCreationForm,
} from "./components";

const BREADCRUMBS = [
  {
    value: "finops-kpi",
    label: "KPIs",
  },
];

export function FinopsKpiPage() {
  const [isConfiguratorOpened, setIsConfiguratorOpened] = useState(false);
  const [editableTarget, setEditableTarget] = useState<KpiTarget | null>();

  const {
    businessContexts,
    primaryBusinessContextId,
    loading: isBusinessContextLoading,
    error: businessContextError,
  } = useBusinessContexts();

  const { addTarget, loading: isAddTargetLoading } = useAddKpiTarget();

  const { updateTarget, loading: isUpdateTargetLoading } = useUpdateKpiTarget();

  const {
    state: {
      kpiType = KpiType.COST_ALLOCATION_COVERAGE,
      orgUnit,
      businessContext,
      period = KpiTimePeriod.ThreeMonths,
    },
    set,
    setQueryState,
    removeFields,
  } = useQueryState<KpiQueryState>();

  const selectedBusinessContext = useMemo(
    () =>
      businessContexts.find(
        ({ value }) => (businessContext ?? primaryBusinessContextId) === value
      ),
    [businessContexts, businessContext, primaryBusinessContextId]
  );
  const bcId = selectedBusinessContext?.value ?? "";

  const {
    mappedKpiData,
    mappedWorstOrgUnits,
    orgUnitName,
    currentTarget,
    isKpiLoading,
    kpiError,
  } = useKpiData({
    bcId,
    period,
    kpiType,
    skip: !selectedBusinessContext,
    orgUnit,
  });

  const { label } = getKpiType(kpiType);

  const onSubmit = (input: KPITargetInput) => {
    if (editableTarget == null) {
      void addTarget(
        input,
        {
          bcId,
          selection: KpiTargetSelection.Upcoming,
          ...getKpiFetchConfig(kpiType),
        },
        handleConfiguratorClose
      );
    } else {
      const targetId = editableTarget.id;
      void updateTarget(
        targetId,
        {
          name: input.name,
          goal: input.goal,
          contactPersonId: input.contactPersonId,
        },
        handleConfiguratorClose
      );
    }
  };

  const currentTargetTime = currentTarget
    ? [
        dayjs(currentTarget.startDate).format(dateFormat.monthShort),
        dayjs(currentTarget.endDate).format(dateFormat.monthShort),
      ]
    : undefined;

  const handleOrgUnitSelect = ({ id }: { id: string }) => {
    if (orgUnit === id) {
      removeFields(["orgUnit"]);
    } else {
      set("orgUnit")(id);
    }
  };

  const { archiveTarget } = useArchiveTarget();

  const handleArchiveTarget = (
    id: string,
    selection = KpiTargetSelection.Archived
  ) => {
    void archiveTarget(
      {
        id,
      },
      {
        bcId,
        selection,
        ...getKpiFetchConfig(kpiType),
      }
    );
  };

  const handleEditTarget = (kpi?: KpiTarget) => {
    setEditableTarget(kpi ?? currentTarget);
    setIsConfiguratorOpened(true);
  };

  const handleConfiguratorClose = () => {
    setIsConfiguratorOpened(false);
    setEditableTarget(null);
  };

  return (
    <>
      <BreadcrumbsBox title="KPIs" labels={BREADCRUMBS} />
      <DataDisplayWrapper
        messageComponent={<NoKPI />}
        hasData={
          isNotNil(selectedBusinessContext) &&
          isNotEmpty(primaryBusinessContextId)
        }
        renderNoData={(message) => (
          <Box>
            <NoData message={message} />
          </Box>
        )}
      >
        <Box mb={theme.spacing.spacing04}>
          <UniversalWrap
            loaderProps={{
              loading: isBusinessContextLoading,
              Component: BrickLoader,
              viewBox: "0 0 10 1",
            }}
            errorProps={{
              error: businessContextError,
            }}
          >
            <section id="flow-kpi-10">
              <Text weight="medium" mb={theme.spacing.spacing02}>
                Business Context
              </Text>
              <Select
                name="Business context"
                options={businessContexts}
                value={selectedBusinessContext}
                onChange={(selectedBc) =>
                  setQueryState({
                    businessContext: selectedBc?.value,
                    kpiType,
                    period,
                  })
                }
              />
            </section>
            <Spacer height={theme.spacing.spacing04} />
          </UniversalWrap>
          <section id="flow-kpi-20">
            <KpiWidget businessContextId={bcId} selectedType={kpiType} />
          </section>
          <Spacer height={theme.spacing.spacing04} />
          <section id="flow-kpi-30">
            <KpiDashboardChart
              kpiType={kpiType}
              data={mappedKpiData}
              error={kpiError}
              loading={isKpiLoading}
              period={period ?? KpiTimePeriod.ThreeMonths}
              setPeriod={(input) => set("period")(input)}
              orgUnit={{
                id: orgUnit,
                name: orgUnitName,
              }}
            />
          </section>
          <Spacer height={theme.spacing.spacing04} />
          <When condition={kpiType === KpiType.COST_ALLOCATION_COVERAGE}>
            <section id="flow-kpi-40">
              <KpiMaturityLevel
                value={mappedKpiData?.currentValue ?? 0}
                loading={isKpiLoading}
              />
              <Spacer height={theme.spacing.spacing02} />
            </section>
          </When>
          <When condition={kpiType !== KpiType.COST_ALLOCATION_COVERAGE}>
            <WorstOrgUnits
              orgUnits={mappedWorstOrgUnits}
              selectedOrgUnit={orgUnit}
              kpiType={kpiType}
              loading={isKpiLoading}
              businessContextId={bcId}
              onClick={handleOrgUnitSelect}
            />
            <Spacer height={theme.spacing.spacing06} />
          </When>
          <section id="flow-kpi-50">
            <CurrentTarget
              targetData={currentTarget}
              loading={isKpiLoading}
              kpiType={kpiType}
              onAddNewTarget={() => setIsConfiguratorOpened(true)}
              onArchiveTarget={handleArchiveTarget}
              onEditTarget={() => handleEditTarget()}
            />
          </section>
        </Box>
        <Sidebar
          isOpen={isConfiguratorOpened}
          width="38rem"
          title={getSidebarTitle(
            label,
            editableTarget != null,
            editableTarget?.id === currentTarget?.id
          )}
          onClick={handleConfiguratorClose}
        >
          <TargetCreationForm
            kpiType={kpiType}
            businessContextId={bcId}
            loading={isAddTargetLoading || isUpdateTargetLoading}
            currentTargetTime={currentTargetTime}
            editableTarget={editableTarget}
            onSubmit={onSubmit}
          />
        </Sidebar>
        <section id="flow-kpi-60">
          <KpiTargets
            selectedBusinessContextId={bcId}
            kpiType={kpiType}
            onArchiveTarget={handleArchiveTarget}
            onEditTarget={(kpi) => handleEditTarget(kpi)}
          />
        </section>
      </DataDisplayWrapper>
    </>
  );
}

function getSidebarTitle(
  kpiName: string,
  isEditing: boolean,
  isCurrent: boolean
) {
  const targetType = isCurrent ? "Current Target" : "Target";
  const base = `KPI ${targetType} for ${kpiName}`;
  const prefix = "Editing";

  return isEditing ? `${prefix} ${base}` : base;
}
